import type { BackendMenu } from '../../services/api'

export interface NavLeaf {
  kind: 'item'
  code: string
  label: string
  path: string
  icon: string | null
}

export interface NavFolder {
  kind: 'folder'
  code: string
  label: string
  icon: string | null
  items: NavLeaf[]
}

export type NavNode = NavLeaf | NavFolder

export interface NavSection {
  code: string
  title: string
  nodes: NavNode[]
}

/** A leaf plus where it sits, used to render search results out of context. */
export interface NavSearchHit {
  leaf: NavLeaf
  /** e.g. ["SPMB", "Observasi & Seleksi"] */
  trail: string[]
}

type PermissionCheck = (permission: string) => boolean

function isVisible(menu: BackendMenu, hasPermission: PermissionCheck): boolean {
  if (!menu.permissions || menu.permissions.length === 0) return true
  return menu.permissions.some(hasPermission)
}

function toLeaf(menu: BackendMenu): NavLeaf {
  return {
    kind: 'item',
    code: menu.code,
    label: menu.name,
    path: menu.path || '#',
    icon: menu.icon,
  }
}

/**
 * Turns the flat menu records into sections holding items and one optional
 * level of folders. Anything the user cannot reach is dropped, and folders or
 * sections left empty by that filtering disappear rather than render blank.
 */
export function buildNavTree(
  menus: BackendMenu[],
  hasPermission: PermissionCheck,
): NavSection[] {
  const byParent = new Map<string | null, BackendMenu[]>()
  for (const menu of menus) {
    const key = menu.parentId ?? null
    const siblings = byParent.get(key)
    if (siblings) siblings.push(menu)
    else byParent.set(key, [menu])
  }
  for (const siblings of byParent.values()) {
    siblings.sort((a, b) => a.sortOrder - b.sortOrder)
  }

  const sections: NavSection[] = []
  for (const section of byParent.get(null) ?? []) {
    if (section.menuType !== 'group') continue

    const nodes: NavNode[] = []
    for (const child of byParent.get(section.id) ?? []) {
      if (child.menuType === 'item') {
        if (isVisible(child, hasPermission)) nodes.push(toLeaf(child))
        continue
      }
      if (child.menuType !== 'group') continue

      const items = (byParent.get(child.id) ?? [])
        .filter((grandChild) => grandChild.menuType === 'item')
        .filter((grandChild) => isVisible(grandChild, hasPermission))
        .map(toLeaf)
      if (items.length > 0) {
        nodes.push({
          kind: 'folder',
          code: child.code,
          label: child.name,
          icon: child.icon,
          items,
        })
      }
    }

    if (nodes.length > 0) sections.push({ code: section.code, title: section.name, nodes })
  }

  return sections
}

/** Every leaf in order, flattened out of its folders. */
export function flattenLeaves(sections: NavSection[]): NavLeaf[] {
  return sections.flatMap((section) =>
    section.nodes.flatMap((node) => (node.kind === 'item' ? [node] : node.items)),
  )
}

/**
 * Searches the tree the user can already see. A folder or section name counts
 * as a match for everything inside it, so typing "akses" surfaces the whole
 * folder rather than nothing.
 */
export function searchNavTree(sections: NavSection[], rawQuery: string): NavSearchHit[] {
  const query = rawQuery.trim().toLowerCase()
  if (!query) return []

  const hits: NavSearchHit[] = []
  const push = (leaf: NavLeaf, trail: string[]) => {
    hits.push({ leaf, trail })
  }

  for (const section of sections) {
    const sectionMatches = section.title.toLowerCase().includes(query)
    for (const node of section.nodes) {
      if (node.kind === 'item') {
        if (sectionMatches || node.label.toLowerCase().includes(query)) {
          push(node, [section.title])
        }
        continue
      }
      const folderMatches = node.label.toLowerCase().includes(query)
      for (const leaf of node.items) {
        if (sectionMatches || folderMatches || leaf.label.toLowerCase().includes(query)) {
          push(leaf, [section.title, node.label])
        }
      }
    }
  }

  return hits
}

/** Folder codes that contain the given path, so they can open automatically. */
export function foldersContainingPath(sections: NavSection[], pathname: string): string[] {
  return sections.flatMap((section) =>
    section.nodes
      .filter(
        (node): node is NavFolder =>
          node.kind === 'folder' &&
          node.items.some((leaf) => leaf.path !== '#' && pathname.startsWith(leaf.path)),
      )
      .map((folder) => folder.code),
  )
}
